import React, { createContext, useContext, type ReactNode } from 'react';
import {
  EMPTY_ACTIVE_DOCUMENT_INFO,
  INERT_ACTIVE_DOCUMENT_REFRESH,
  useActiveDocumentInfoState,
  type ActiveDocumentInfo,
  type ActiveDocumentRefreshState,
} from './useActiveDocumentName';

const ActiveDocumentInfoContext = createContext<ActiveDocumentInfo>(EMPTY_ACTIVE_DOCUMENT_INFO);
const ActiveDocumentNameContext = createContext<string | null>(null);
const ActiveProjectKeyContext = createContext<string | null>(null);
const ActiveDocumentRefreshContext =
  createContext<ActiveDocumentRefreshState>(INERT_ACTIVE_DOCUMENT_REFRESH);

/**
 * Single subscription to active Revit document identity for the whole app subtree.
 */
export function ActiveDocumentProvider({ children }: { children: ReactNode }) {
  const { documentInfo, refreshState } = useActiveDocumentInfoState();
  return (
    <ActiveDocumentInfoContext.Provider value={documentInfo}>
      <ActiveDocumentNameContext.Provider value={documentInfo.documentName}>
        <ActiveProjectKeyContext.Provider value={documentInfo.projectKey}>
          <ActiveDocumentRefreshContext.Provider value={refreshState}>
            {children}
          </ActiveDocumentRefreshContext.Provider>
        </ActiveProjectKeyContext.Provider>
      </ActiveDocumentNameContext.Provider>
    </ActiveDocumentInfoContext.Provider>
  );
}

export function useActiveDocumentContext(): string | null {
  return useContext(ActiveDocumentNameContext);
}

export function useActiveDocumentInfoContext(): ActiveDocumentInfo {
  return useContext(ActiveDocumentInfoContext);
}

export function useActiveProjectKey(): string | null {
  return useContext(ActiveProjectKeyContext);
}

/**
 * Manual-refresh controls for model-derived data. ModelChanged events only mark
 * data as stale; pages reload it when the user clicks their Refresh action.
 */
export function useActiveDocumentRefresh(): ActiveDocumentRefreshState {
  return useContext(ActiveDocumentRefreshContext);
}

export type { ActiveDocumentInfo };
export type { ActiveDocumentRefreshState };
